import React, { useState, useEffect } from 'react';
import { tenantsApi, plansApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Buildings, Plus, Power, Eye, Upload, Image, PencilSimple, Users, UserCircle, Key } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const HOSPITAL_TYPES = [
  { value: 'multi_specialty', label: 'Multi-Specialty' },
  { value: 'single_specialty', label: 'Single-Specialty' },
  { value: 'clinic', label: 'Clinic' },
  { value: 'nursing_home', label: 'Nursing Home' },
  { value: 'diagnostic_center', label: 'Diagnostic Center' },
];

const ALL_MODULES = [
  { value: 'claims', label: 'Claims' },
  { value: 'patients', label: 'Patients' },
  { value: 'payers', label: 'Payers/TPA' },
  { value: 'preauth', label: 'Pre-Auth' },
  { value: 'denials', label: 'Denials' },
  { value: 'ar', label: 'AR & Collections' },
  { value: 'payments', label: 'Payments' },
  { value: 'reports', label: 'Reports' },
  { value: 'display_board', label: 'Display Board' },
  { value: 'workflow', label: 'Workflow' },
];

const ONBOARDING = [
  { value: 'pending', label: 'Pending', color: 'bg-slate-100 text-slate-600' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
];

const ROLE_LABELS = {
  hospital_admin: { label: 'Hospital Admin', color: 'bg-blue-100 text-blue-800' },
  claim_manager: { label: 'Claim Manager', color: 'bg-green-100 text-green-800' },
  billing_executive: { label: 'Billing Exec', color: 'bg-orange-100 text-orange-800' },
  tpa_desk: { label: 'TPA Desk', color: 'bg-yellow-100 text-yellow-800' },
  ar_executive: { label: 'AR Executive', color: 'bg-pink-100 text-pink-800' },
  finance_manager: { label: 'Finance Mgr', color: 'bg-indigo-100 text-indigo-800' },
  management_viewer: { label: 'Mgmt Viewer', color: 'bg-teal-100 text-teal-800' },
  reception_user: { label: 'Reception', color: 'bg-cyan-100 text-cyan-800' },
};

const emptyForm = {
  name: '', code: '', hospital_type: '', multi_branch: false,
  address: '', city: '', state: '', country: 'India', pincode: '',
  phone: '', email: '', website: '',
  gst_number: '', pan_number: '', registration_number: '',
  authorized_person: '', authorized_person_phone: '', authorized_person_email: '',
  admin_name: '', admin_mobile: '', admin_email: '',
  subscription_plan: 'basic', plan_start_date: '', plan_end_date: '',
  max_users: 10, max_branches: 3, max_storage_gb: 5,
  timezone: 'Asia/Kolkata', currency: 'INR', financial_year_start: 'April',
  modules_enabled: ['claims', 'patients', 'payers', 'preauth', 'denials', 'ar', 'payments', 'reports', 'display_board', 'workflow'],
  onboarding_status: 'pending',
};

const TenantsPage = () => {
  const [tenants, setTenants] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [detailTab, setDetailTab] = useState('details');
  const [hospitalUsers, setHospitalUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [form, setForm] = useState({...emptyForm});

  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tenantsRes, plansRes] = await Promise.all([
        tenantsApi.list(),
        plansApi.list(),
      ]);
      setTenants(tenantsRes.data);
      setAvailablePlans(plansRes.data.filter(p => p.is_active));
    } catch (err) {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.name) { toast.error('Hospital name is required'); return; }
    try {
      const payload = {
        ...form,
        max_users: parseInt(form.max_users),
        max_branches: parseInt(form.max_branches),
        max_storage_gb: parseInt(form.max_storage_gb),
      };
      const res = await tenantsApi.create(payload);
      toast.success(`Hospital "${form.name}" registered! Admin: ${res.data.admin_email}`);
      setDialogOpen(false);
      setForm({...emptyForm});
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register hospital');
    }
  };

  const handleToggle = async (id, name, currentActive) => {
    try {
      await tenantsApi.toggle(id);
      toast.success(`${name} ${currentActive ? 'deactivated' : 'activated'}`);
      loadData();
    } catch { toast.error('Failed to toggle hospital'); }
  };

  const openDetail = async (t) => {
    setDetailDialog(t);
    setDetailTab('details');
    setEditMode(false);
    setHospitalUsers([]);
  };

  const loadHospitalUsers = async (tenantId) => {
    setUsersLoading(true);
    try {
      const res = await tenantsApi.getUsers(tenantId);
      setHospitalUsers(res.data);
    } catch (err) {
      toast.error('Failed to load hospital users');
    } finally { setUsersLoading(false); }
  };

  const handleTabChange = (tab) => {
    setDetailTab(tab);
    if (tab === 'users' && detailDialog) {
      loadHospitalUsers(detailDialog.id);
    }
    if (tab === 'edit' && detailDialog) {
      setEditForm({
        name: detailDialog.name || '',
        hospital_type: detailDialog.hospital_type || '',
        phone: detailDialog.phone || '',
        email: detailDialog.email || '',
        website: detailDialog.website || '',
        address: detailDialog.address || '',
        city: detailDialog.city || '',
        state: detailDialog.state || '',
        pincode: detailDialog.pincode || '',
        gst_number: detailDialog.gst_number || '',
        pan_number: detailDialog.pan_number || '',
        subscription_plan: detailDialog.subscription_plan || 'basic',
        max_users: detailDialog.max_users || 10,
        max_branches: detailDialog.max_branches || 3,
        max_storage_gb: detailDialog.max_storage_gb || 5,
        onboarding_status: detailDialog.onboarding_status || 'pending',
        modules_enabled: detailDialog.modules_enabled || [],
        admin_email: detailDialog.admin_email || '',
      });
      setEditMode(true);
    }
  };

  const handleEditSave = async () => {
    if (!detailDialog) return;
    try {
      await tenantsApi.update(detailDialog.id, {
        ...detailDialog,
        ...editForm,
        max_users: parseInt(editForm.max_users),
        max_branches: parseInt(editForm.max_branches),
        max_storage_gb: parseInt(editForm.max_storage_gb),
      });
      if (editForm.onboarding_status !== detailDialog.onboarding_status) {
        await tenantsApi.updateOnboarding(detailDialog.id, editForm.onboarding_status);
      }
      // Update admin email if changed
      if (editForm.admin_email && editForm.admin_email !== detailDialog.admin_email) {
        await tenantsApi.updateAdminEmail(detailDialog.id, editForm.admin_email);
      }
      toast.success('Hospital updated successfully');
      setDetailDialog(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleResetAdminPassword = async () => {
    if (!detailDialog) return;
    if (!window.confirm(`Reset password for ${detailDialog.admin_email}? A temporary password will be generated and emailed.`)) return;
    setResetLoading(true);
    try {
      const res = await tenantsApi.resetAdminPassword(detailDialog.id);
      const data = res.data;
      if (data.email_sent) {
        toast.success(data.message);
      } else {
        toast.success(`Password reset. Temporary: ${data.temp_password} (SMTP not configured - email not sent)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally { setResetLoading(false); }
  };

  const getPlanBadge = (planCode) => {
    const PLAN_COLORS = { trial: '#F97316', basic: '#94A3B8', standard: '#0EA5E9', professional: '#8B5CF6', enterprise: '#F59E0B' };
    const p = availablePlans.find(x => x.code === planCode);
    const color = PLAN_COLORS[planCode] || '#94A3B8';
    return <Badge className="border-0 text-xs" style={{ backgroundColor: `${color}18`, color }}>{p?.name || planCode}</Badge>;
  };

  const toggleModule = (mod, formSetter) => {
    formSetter(f => ({
      ...f,
      modules_enabled: f.modules_enabled.includes(mod)
        ? f.modules_enabled.filter(m => m !== mod)
        : [...f.modules_enabled, mod]
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="tenants-title">Hospital Accounts</h1>
          <p className="text-sm text-slate-500 mt-1">Register, manage, and configure hospital accounts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white shadow-lg shadow-sky-500/25 gap-2" data-testid="register-hospital-btn">
              <Plus className="h-4 w-4" /> Register Hospital
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Outfit'] text-xl">Register New Hospital</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 pt-2">
              {/* Hospital Identity */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 border-b pb-2">Hospital Identity</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2"><Label>Hospital Name *</Label>
                    <Input placeholder="ABC Multi-Specialty Hospital" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="hospital-name-input" /></div>
                  <div><Label>Code</Label>
                    <Input placeholder="Auto" value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value}))} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div><Label>Hospital Type</Label>
                    <Select value={form.hospital_type} onValueChange={v => setForm(f => ({...f, hospital_type: v}))}>
                      <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>{HOSPITAL_TYPES.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Registration No.</Label>
                    <Input value={form.registration_number} onChange={e => setForm(f => ({...f, registration_number: e.target.value}))} /></div>
                  <div className="flex items-end gap-2 pb-1">
                    <Checkbox id="multi-branch" checked={form.multi_branch} onCheckedChange={v => setForm(f => ({...f, multi_branch: v}))} />
                    <Label htmlFor="multi-branch" className="text-sm cursor-pointer">Multi-Branch Hospital</Label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div><Label>GST Number</Label>
                    <Input placeholder="22AAAAA0000A1Z5" value={form.gst_number} onChange={e => setForm(f => ({...f, gst_number: e.target.value}))} /></div>
                  <div><Label>PAN Number</Label>
                    <Input placeholder="AAAAA0000A" value={form.pan_number} onChange={e => setForm(f => ({...f, pan_number: e.target.value}))} /></div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 border-b pb-2">Contact Details</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Hospital Email</Label>
                    <Input type="email" placeholder="info@hospital.com" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="hospital-email" /></div>
                  <div><Label>Phone</Label>
                    <Input placeholder="+91 98765 43210" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} /></div>
                  <div><Label>Website</Label>
                    <Input placeholder="www.hospital.com" value={form.website} onChange={e => setForm(f => ({...f, website: e.target.value}))} /></div>
                </div>
                <div className="mt-3"><Label>Address</Label>
                  <Input placeholder="Full address" value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} /></div>
                <div className="grid grid-cols-4 gap-3 mt-3">
                  <div><Label>City</Label><Input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} /></div>
                  <div><Label>State</Label><Input value={form.state} onChange={e => setForm(f => ({...f, state: e.target.value}))} /></div>
                  <div><Label>Country</Label><Input value={form.country} onChange={e => setForm(f => ({...f, country: e.target.value}))} /></div>
                  <div><Label>Pincode</Label><Input value={form.pincode} onChange={e => setForm(f => ({...f, pincode: e.target.value}))} /></div>
                </div>
              </div>

              {/* Authorized Person */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 border-b pb-2">Authorized Person</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Name</Label><Input value={form.authorized_person} onChange={e => setForm(f => ({...f, authorized_person: e.target.value}))} /></div>
                  <div><Label>Mobile</Label><Input value={form.authorized_person_phone} onChange={e => setForm(f => ({...f, authorized_person_phone: e.target.value}))} /></div>
                  <div><Label>Email</Label><Input type="email" value={form.authorized_person_email} onChange={e => setForm(f => ({...f, authorized_person_email: e.target.value}))} /></div>
                </div>
              </div>

              {/* Hospital Admin */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 border-b pb-2">Hospital Admin (Login will be created)</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Admin Name</Label><Input placeholder="Admin Full Name" value={form.admin_name} onChange={e => setForm(f => ({...f, admin_name: e.target.value}))} data-testid="admin-name-input" /></div>
                  <div><Label>Admin Mobile</Label><Input placeholder="+91 99999 88888" value={form.admin_mobile} onChange={e => setForm(f => ({...f, admin_mobile: e.target.value}))} /></div>
                  <div><Label>Admin Email *</Label><Input type="email" placeholder="admin@hospital.com" value={form.admin_email} onChange={e => setForm(f => ({...f, admin_email: e.target.value}))} data-testid="admin-email-input" /></div>
                </div>
                <p className="text-xs text-slate-400 mt-2">Password will be auto-generated as: [HOSPITAL_CODE]@2024</p>
              </div>

              {/* Subscription & Licensing */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 border-b pb-2">Subscription & Licensing</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Plan</Label>
                    <Select value={form.subscription_plan} onValueChange={v => {
                      const selected = availablePlans.find(p => p.code === v);
                      setForm(f => ({
                        ...f,
                        subscription_plan: v,
                        max_users: selected?.max_users || f.max_users,
                        max_branches: selected?.max_branches || f.max_branches,
                        max_storage_gb: selected?.max_storage_gb || f.max_storage_gb,
                        modules_enabled: selected?.modules_included || f.modules_enabled,
                      }));
                    }}>
                      <SelectTrigger data-testid="plan-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {availablePlans.map(p => (
                          <SelectItem key={p.code} value={p.code}>{p.name} - {new Intl.NumberFormat('en-IN', {style:'currency', currency:'INR', maximumFractionDigits:0}).format(p.monthly_price)}/mo</SelectItem>
                        ))}
                        {availablePlans.length === 0 && <SelectItem value="basic">Basic</SelectItem>}
                      </SelectContent>
                    </Select></div>
                  <div><Label>Plan Start</Label>
                    <Input type="date" value={form.plan_start_date} onChange={e => setForm(f => ({...f, plan_start_date: e.target.value}))} /></div>
                  <div><Label>Plan End</Label>
                    <Input type="date" value={form.plan_end_date} onChange={e => setForm(f => ({...f, plan_end_date: e.target.value}))} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div><Label>Max Users</Label><Input type="number" value={form.max_users} onChange={e => setForm(f => ({...f, max_users: e.target.value}))} /></div>
                  <div><Label>Max Branches</Label><Input type="number" value={form.max_branches} onChange={e => setForm(f => ({...f, max_branches: e.target.value}))} /></div>
                  <div><Label>Storage (GB)</Label><Input type="number" value={form.max_storage_gb} onChange={e => setForm(f => ({...f, max_storage_gb: e.target.value}))} /></div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div><Label>Timezone</Label><Input value={form.timezone} onChange={e => setForm(f => ({...f, timezone: e.target.value}))} /></div>
                  <div><Label>Currency</Label><Input value={form.currency} onChange={e => setForm(f => ({...f, currency: e.target.value}))} /></div>
                  <div><Label>Financial Year Start</Label>
                    <Select value={form.financial_year_start} onValueChange={v => setForm(f => ({...f, financial_year_start: v}))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="January">January</SelectItem>
                        <SelectItem value="April">April</SelectItem>
                      </SelectContent>
                    </Select></div>
                </div>
              </div>

              {/* Module Licensing */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 border-b pb-2">Module Licensing</h3>
                <div className="grid grid-cols-5 gap-2">
                  {ALL_MODULES.map(mod => (
                    <div key={mod.value} className="flex items-center gap-2">
                      <Checkbox id={`mod-${mod.value}`} checked={form.modules_enabled.includes(mod.value)}
                        onCheckedChange={() => toggleModule(mod.value, setForm)} />
                      <Label htmlFor={`mod-${mod.value}`} className="text-xs cursor-pointer">{mod.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white mt-2" onClick={handleCreate} data-testid="submit-hospital-btn">
                Register Hospital & Create Admin
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Hospital List Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="hospitals-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Hospital</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Plan</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Admin</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Users</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Branches</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Onboarding</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((t) => (
                  <TableRow key={t.id} className="hover:bg-sky-50/50 transition-colors cursor-pointer" data-testid={`hospital-row-${t.id}`}
                    onClick={() => openDetail(t)}>
                    <TableCell>
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{t.name}</p>
                        <p className="text-xs text-slate-400">{t.code} {t.city ? `| ${t.city}` : ''}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 capitalize">{(t.hospital_type || '-').replace('_', ' ')}</TableCell>
                    <TableCell>{getPlanBadge(t.subscription_plan)}</TableCell>
                    <TableCell className="text-sm text-violet-600">{t.admin_email || '-'}</TableCell>
                    <TableCell className="text-center text-sm">
                      <span className="font-medium">{t.user_count || 0}</span>
                      <span className="text-slate-400">/{t.max_users}</span>
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      <span className="font-medium">{t.branch_count || 0}</span>
                      <span className="text-slate-400">/{t.max_branches}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`border-0 text-xs ${
                        (ONBOARDING.find(o => o.value === t.onboarding_status) || ONBOARDING[0]).color
                      }`}>
                        {(t.onboarding_status || 'pending').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${t.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'} border-0 text-xs`}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openDetail(t)} className="hover:bg-sky-50 text-sky-600" data-testid={`view-hospital-${t.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { openDetail(t); setTimeout(() => handleTabChange('edit'), 100); }}
                          className="hover:bg-violet-50 text-violet-600" data-testid={`edit-hospital-${t.id}`}>
                          <PencilSimple className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggle(t.id, t.name, t.is_active)}
                          className={`${t.is_active ? 'hover:bg-red-50 text-red-500' : 'hover:bg-emerald-50 text-emerald-600'}`}
                          data-testid={`toggle-hospital-${t.id}`}>
                          <Power className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {tenants.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-500">
                    <Buildings className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">No hospitals registered yet</p>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Hospital Detail Dialog with Tabs */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-lg">{detailDialog?.name}</DialogTitle></DialogHeader>
          {detailDialog && (
            <div className="space-y-4">
              {/* Tab Navigation */}
              <div className="flex gap-1 border-b border-slate-200" data-testid="detail-tabs">
                {[
                  { key: 'details', label: 'Details', icon: Eye },
                  { key: 'users', label: 'Users', icon: Users },
                  { key: 'edit', label: 'Edit', icon: PencilSimple },
                ].map(tab => (
                  <button key={tab.key}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-[1px] ${
                      detailTab === tab.key
                        ? 'border-sky-500 text-sky-600'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                    onClick={() => handleTabChange(tab.key)}
                    data-testid={`tab-${tab.key}`}
                  >
                    <tab.icon className="h-4 w-4" /> {tab.label}
                  </button>
                ))}
              </div>

              {/* Details Tab */}
              {detailTab === 'details' && (
                <div className="space-y-4">
                  {/* Logo */}
                  <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="hospital-logo-section">
                    <div className="h-16 w-16 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {detailDialog.logo_url ? (
                        <img src={detailDialog.logo_url} alt="Logo" className="h-full w-full object-contain" />
                      ) : (
                        <Image className="h-8 w-8 text-slate-300" weight="duotone" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-700">Hospital Logo</p>
                      <p className="text-xs text-slate-500 mb-2">Used on Display Board & branding</p>
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            const res = await tenantsApi.uploadLogo(detailDialog.id, fd);
                            toast.success('Logo uploaded!');
                            setDetailDialog(d => ({ ...d, logo_url: res.data.logo_url }));
                            loadData();
                          } catch { toast.error('Upload failed'); }
                        }} data-testid="logo-upload-input" />
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-md text-xs font-medium text-slate-700 hover:bg-slate-50">
                          <Upload className="h-3.5 w-3.5" /> Upload Logo
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Account Info */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-400 text-xs block">Hospital Name</span><p className="font-medium text-slate-800">{detailDialog.name}</p></div>
                    <div><span className="text-slate-400 text-xs block">Code</span><p className="font-mono text-sky-700">{detailDialog.code}</p></div>
                    <div><span className="text-slate-400 text-xs block">Type</span><p className="capitalize">{(detailDialog.hospital_type || '-').replace('_', ' ')}</p></div>
                    <div><span className="text-slate-400 text-xs block">Multi-Branch</span><p>{detailDialog.multi_branch ? 'Yes' : 'No'}</p></div>
                    <div><span className="text-slate-400 text-xs block">GST</span><p>{detailDialog.gst_number || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">PAN</span><p>{detailDialog.pan_number || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Registration</span><p>{detailDialog.registration_number || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Plan</span>{getPlanBadge(detailDialog.subscription_plan)}</div>
                    <div><span className="text-slate-400 text-xs block">Hospital Admin</span><p className="text-violet-600">{detailDialog.admin_email || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Phone</span><p>{detailDialog.phone || '-'}</p></div>
                    <div><span className="text-slate-400 text-xs block">Onboarding</span>
                      <Badge className={`border-0 text-xs ${(ONBOARDING.find(o => o.value === detailDialog.onboarding_status) || ONBOARDING[0]).color}`}>
                        {(detailDialog.onboarding_status || 'pending').replace('_',' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </Badge></div>
                    <div><span className="text-slate-400 text-xs block">Timezone</span><p>{detailDialog.timezone || 'Asia/Kolkata'}</p></div>
                    <div className="col-span-2"><span className="text-slate-400 text-xs block">Address</span><p>{[detailDialog.address, detailDialog.city, detailDialog.state, detailDialog.pincode].filter(Boolean).join(', ') || '-'}</p></div>
                  </div>

                  {/* Account Limits */}
                  <div className="grid grid-cols-3 gap-3">
                    <Card className="bg-sky-50 border-sky-200"><CardContent className="pt-3 pb-3 text-center">
                      <p className="text-xs text-sky-600 font-semibold">Users</p>
                      <p className="text-xl font-bold text-sky-800">{detailDialog.user_count || 0}<span className="text-xs text-sky-500">/{detailDialog.max_users || 10}</span></p>
                    </CardContent></Card>
                    <Card className="bg-violet-50 border-violet-200"><CardContent className="pt-3 pb-3 text-center">
                      <p className="text-xs text-violet-600 font-semibold">Branches</p>
                      <p className="text-xl font-bold text-violet-800">{detailDialog.branch_count || 0}<span className="text-xs text-violet-500">/{detailDialog.max_branches || 3}</span></p>
                    </CardContent></Card>
                    <Card className="bg-amber-50 border-amber-200"><CardContent className="pt-3 pb-3 text-center">
                      <p className="text-xs text-amber-600 font-semibold">Storage</p>
                      <p className="text-xl font-bold text-amber-800">{detailDialog.max_storage_gb || 5}<span className="text-xs text-amber-500"> GB</span></p>
                    </CardContent></Card>
                  </div>

                  {/* Licensed Modules */}
                  <div>
                    <p className="text-xs text-slate-400 mb-1.5">Licensed Modules</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(detailDialog.modules_enabled || []).map(m => (
                        <Badge key={m} className="bg-sky-50 text-sky-700 border-0 text-xs capitalize">{m.replace('_', ' ')}</Badge>
                      ))}
                      {(detailDialog.modules_enabled || []).length === 0 && <span className="text-xs text-slate-400">No modules</span>}
                    </div>
                  </div>

                  {/* Admin Actions */}
                  {detailDialog.admin_email && (
                    <div className="pt-2 border-t border-slate-200">
                      <Button variant="outline" size="sm" className="text-amber-600 border-amber-200 hover:bg-amber-50 gap-1.5"
                        onClick={handleResetAdminPassword} disabled={resetLoading} data-testid="reset-admin-password-btn">
                        <Key className="h-4 w-4" /> {resetLoading ? 'Resetting...' : 'Reset Admin Password'}
                      </Button>
                      <p className="text-xs text-slate-400 mt-1.5">Generates a temporary password and emails it to {detailDialog.admin_email}. Admin will be forced to change it on next login.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Users Tab */}
              {detailTab === 'users' && (
                <div className="space-y-3" data-testid="hospital-users-tab">
                  <p className="text-sm text-slate-500">Users belonging to <strong>{detailDialog.name}</strong></p>
                  {usersLoading ? (
                    <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-500"></div></div>
                  ) : hospitalUsers.length === 0 ? (
                    <div className="text-center py-8">
                      <UserCircle className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm text-slate-400">No users found for this hospital</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-xs">User</TableHead>
                          <TableHead className="text-xs">Role</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          <TableHead className="text-xs">Created</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {hospitalUsers.map(u => {
                          const rl = ROLE_LABELS[u.role] || { label: u.role, color: 'bg-slate-100 text-slate-600' };
                          return (
                            <TableRow key={u.id} data-testid={`tenant-user-${u.id}`}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-sky-400 to-violet-400 flex items-center justify-center text-white text-xs font-semibold">
                                    {u.name?.charAt(0)?.toUpperCase() || 'U'}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-800">{u.name}</p>
                                    <p className="text-xs text-slate-400">{u.email}</p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell><Badge className={`${rl.color} border-0 text-xs`}>{rl.label}</Badge></TableCell>
                              <TableCell>
                                <Badge className={`border-0 text-xs ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                  {u.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              )}

              {/* Edit Tab */}
              {detailTab === 'edit' && editMode && (
                <div className="space-y-4" data-testid="hospital-edit-tab">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Hospital Name</Label>
                      <Input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} /></div>
                    <div><Label>Hospital Type</Label>
                      <Select value={editForm.hospital_type} onValueChange={v => setEditForm(f => ({...f, hospital_type: v}))}>
                        <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>{HOSPITAL_TYPES.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
                      </Select></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Phone</Label>
                      <Input value={editForm.phone} onChange={e => setEditForm(f => ({...f, phone: e.target.value}))} /></div>
                    <div><Label>Email</Label>
                      <Input type="email" value={editForm.email} onChange={e => setEditForm(f => ({...f, email: e.target.value}))} /></div>
                  </div>
                  <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                    <Label className="text-violet-700 text-xs font-semibold">Hospital Admin Login Email</Label>
                    <Input type="email" value={editForm.admin_email} onChange={e => setEditForm(f => ({...f, admin_email: e.target.value}))}
                      className="mt-1 border-violet-200" placeholder="admin@hospital.com" data-testid="edit-admin-email" />
                    <p className="text-[10px] text-violet-500 mt-1">This is the email used by the Hospital Admin to log in</p>
                  </div>
                  <div><Label>Address</Label>
                    <Input value={editForm.address} onChange={e => setEditForm(f => ({...f, address: e.target.value}))} /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>City</Label><Input value={editForm.city} onChange={e => setEditForm(f => ({...f, city: e.target.value}))} /></div>
                    <div><Label>State</Label><Input value={editForm.state} onChange={e => setEditForm(f => ({...f, state: e.target.value}))} /></div>
                    <div><Label>Pincode</Label><Input value={editForm.pincode} onChange={e => setEditForm(f => ({...f, pincode: e.target.value}))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>GST</Label><Input value={editForm.gst_number} onChange={e => setEditForm(f => ({...f, gst_number: e.target.value}))} /></div>
                    <div><Label>PAN</Label><Input value={editForm.pan_number} onChange={e => setEditForm(f => ({...f, pan_number: e.target.value}))} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Plan</Label>
                      <Select value={editForm.subscription_plan} onValueChange={v => {
                        const selected = availablePlans.find(p => p.code === v);
                        setEditForm(f => ({
                          ...f,
                          subscription_plan: v,
                          max_users: selected?.max_users || f.max_users,
                          max_branches: selected?.max_branches || f.max_branches,
                          max_storage_gb: selected?.max_storage_gb || f.max_storage_gb,
                        }));
                      }}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {availablePlans.map(p => (
                            <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select></div>
                    <div><Label>Max Users</Label><Input type="number" value={editForm.max_users} onChange={e => setEditForm(f => ({...f, max_users: e.target.value}))} /></div>
                    <div><Label>Max Branches</Label><Input type="number" value={editForm.max_branches} onChange={e => setEditForm(f => ({...f, max_branches: e.target.value}))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Storage (GB)</Label><Input type="number" value={editForm.max_storage_gb} onChange={e => setEditForm(f => ({...f, max_storage_gb: e.target.value}))} /></div>
                    <div><Label>Onboarding Status</Label>
                      <Select value={editForm.onboarding_status} onValueChange={v => setEditForm(f => ({...f, onboarding_status: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ONBOARDING.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select></div>
                  </div>
                  {/* Module Licensing */}
                  <div>
                    <Label className="text-xs">Module Licensing</Label>
                    <div className="grid grid-cols-5 gap-2 mt-2">
                      {ALL_MODULES.map(mod => (
                        <div key={mod.value} className="flex items-center gap-2">
                          <Checkbox id={`emod-${mod.value}`} checked={(editForm.modules_enabled || []).includes(mod.value)}
                            onCheckedChange={() => toggleModule(mod.value, setEditForm)} />
                          <Label htmlFor={`emod-${mod.value}`} className="text-xs cursor-pointer">{mod.label}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white" onClick={handleEditSave} data-testid="save-hospital-btn">
                    Save Changes
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantsPage;
